import "@shopify/ui-extensions/preact";
import { render } from "preact";
import { useEffect, useState } from "preact/hooks";
import { BACKEND_URL_FROM_ENV } from "./backend-url.generated";

const EXTENSION_SOURCE = "threshold-free-gift-checkout-sync";
const EXTENSION_TARGET = "purchase.checkout.cart-line-list.render-after";
const THRESHOLD_ATTRIBUTE_KEY = "_ThresholdOffer";
const GIFT_CARD_PRODUCT_TYPE = "gift card";
// The value carries the threshold that granted the gift, e.g. "free_gift_880".
const THRESHOLD_ATTRIBUTE_VALUE_PREFIX = "free_gift_";
const SYNC_RETRY_DELAY_MS = 350;
const CONFIG_RETRY_DELAYS_MS = [500, 1500, 4000, 10000];
// A cold backend or a token refresh can make the first price lookup fail, so
// it is retried briefly before the cart is treated as unresolvable.
const PRICE_RETRY_DELAYS_MS = [300, 900];
// Upper bound on how long checkout may be held while gifts are being
// resolved. Blocking exists so an order cannot complete without a gift the
// cart earned, but it must never strand a buyer when the backend is down.
const MAX_BLOCK_MS = 15000;
const SYNC_PENDING_MESSAGE =
  "Adding your free gift. Please wait a moment and try again.";
const GIFT_PICKER_MODAL_ID = "threshold-gift-picker-modal";
const DEFAULT_GIFT_PICKER = {
  enabled: false,
  heading: "Choose Your Free Gift",
  description:
    "You are eligible for a free gift. Please select your preferred gift below.",
  buttonText: "Choose Your Gift",
};

function getCheckoutApi(args) {
  const candidates = [...args, globalThis.shopify].filter(Boolean);

  return candidates.find(
    (candidate) =>
      typeof candidate.applyCartLinesChange === "function" &&
      candidate.lines &&
      candidate.shop,
  );
}

function getSignalValue(signal, fallback = undefined) {
  return signal?.value ?? signal?.current ?? fallback;
}

function getShopDomain(api) {
  return api?.shop?.myshopifyDomain || "";
}

/**
 * Draft-order checkouts (and some other checkout types) fix the line items, so
 * Shopify rejects `applyCartLinesChange` with "not supported on this checkout".
 * `instructions.lines` says up front which change types are allowed. When the
 * instructions are unavailable the change is assumed allowed, as before.
 */
const LINE_CHANGE_PERMISSION = {
  addCartLine: "canAddCartLine",
  removeCartLine: "canRemoveCartLine",
  updateCartLine: "canUpdateCartLine",
};

// Set once Shopify refuses a line change as unsupported, so this checkout is
// never retried (and never held) again.
let lineChangesLocked = false;

function isDraftOrderCheckout(api) {
  return (
    getSignalValue(api?.checkoutSettings, null)?.orderSubmission ===
    "DRAFT_ORDER"
  );
}

function isUnsupportedLineChangeError(message) {
  return /not supported on this checkout/i.test(String(message ?? ""));
}

function canApplyLineChange(api, type) {
  // Draft order checkouts never accept line changes, even when `instructions`
  // does not say so; their gift lines are synced on the Draft Order by the app.
  if (lineChangesLocked || isDraftOrderCheckout(api)) return false;

  const lines = getSignalValue(api?.instructions, null)?.lines;
  const permission = LINE_CHANGE_PERMISSION[type];

  if (!lines || !permission || typeof lines[permission] !== "boolean") {
    return true;
  }

  return lines[permission];
}

function filterSupportedLineChanges(api, changes) {
  return changes.filter((change) => canApplyLineChange(api, change.type));
}

function getCheckoutToken(api) {
  return getSignalValue(api?.checkoutToken, null) || null;
}

function createThresholdAttribute(thresholdAmount) {
  return {
    key: THRESHOLD_ATTRIBUTE_KEY,
    value: `${THRESHOLD_ATTRIBUTE_VALUE_PREFIX}${thresholdAmount ?? ""}`,
  };
}

function normalizeAttributes(attributes) {
  return Array.isArray(attributes)
    ? attributes.flatMap((attribute) => {
        if (!attribute?.key) return [];

        return [
          {
            key: String(attribute.key),
            value: String(attribute.value ?? ""),
          },
        ];
      })
    : [];
}

function isThresholdGiftAttribute(attribute) {
  return (
    attribute?.key === THRESHOLD_ATTRIBUTE_KEY &&
    String(attribute?.value || "")
      .toLowerCase()
      .startsWith(THRESHOLD_ATTRIBUTE_VALUE_PREFIX)
  );
}

function isThresholdGiftLine(line) {
  return normalizeAttributes(line?.attributes).some(isThresholdGiftAttribute);
}

function withThresholdAttribute(attributes, thresholdAmount) {
  const nextAttributes = normalizeAttributes(attributes).filter(
    (attribute) => attribute.key !== THRESHOLD_ATTRIBUTE_KEY,
  );

  nextAttributes.push(createThresholdAttribute(thresholdAmount));

  return nextAttributes;
}

function getLineAmount(line) {
  const amount = Number(line?.cost?.totalAmount?.amount);
  return Number.isFinite(amount) ? amount : 0;
}

/**
 * The line's presentment amount before any discount, i.e. what Shopify's
 * Markets pricing produced for this variant × quantity. Used to derive the
 * presentment -> shop currency rate without a second price lookup.
 */
function getLineUndiscountedAmount(line) {
  const allocations = Array.isArray(line?.discountAllocations)
    ? line.discountAllocations
    : [];

  return allocations.reduce((sum, allocation) => {
    const discounted = Number(allocation?.discountedAmount?.amount);
    return sum + (Number.isFinite(discounted) ? discounted : 0);
  }, getLineAmount(line));
}

/**
 * Shopify gift card products are identified by their product type.
 */
function isGiftCardProductLine(line) {
  const productType = line?.merchandise?.product?.productType;

  return (
    typeof productType === "string" &&
    productType.trim().toLowerCase() === GIFT_CARD_PRODUCT_TYPE
  );
}

/**
 * Line total converted from the buyer's presentment currency into the shop
 * currency. Each variant has its own rate (Markets rounds prices per
 * product), falling back to the cart-wide rate for anything unknown. Without
 * a conversion, or when both currencies match, the amount is used as-is.
 */
function getLineAmountInShopCurrency(line, pricing) {
  const amount = getLineAmount(line);

  // Same currency (or nothing to convert with): the presentment amount is
  // already the shop-currency amount.
  if (!pricing?.needsConversion) return amount;

  const unitPrice = Number(pricing.prices?.[line?.merchandise?.id]);
  const quantity = Number(line?.quantity);

  if (!(unitPrice >= 0) || !(quantity > 0)) return 0;

  // Keep any discount proportional to the shop-currency line value.
  const undiscounted = getLineUndiscountedAmount(line);
  const discountRatio = undiscounted > 0 ? amount / undiscounted : 1;

  return unitPrice * quantity * discountRatio;
}

/**
 * Threshold base, always in the shop currency so it can be compared with the
 * configured threshold amounts regardless of the buyer's market.
 */
function getThresholdBaseAmount(
  cartLines,
  disallowGiftCardProducts = false,
  pricing = null,
) {
  return cartLines
    .filter((line) => !isThresholdGiftLine(line))
    .filter(
      (line) => !(disallowGiftCardProducts && isGiftCardProductLine(line)),
    )
    .reduce(
      (sum, line) => sum + getLineAmountInShopCurrency(line, pricing),
      0,
    );
}

/**
 * Shop-currency unit prices, read from the app backend on every evaluation.
 * Nothing is cached: a cached miss used to deny a gift for the rest of the
 * session, and a cached hit could outlive a price or currency change. The
 * request is only made when the cart, currency or rules actually change.
 */
function getPricedVariantIds(cartLines) {
  return Array.from(
    new Set(
      (Array.isArray(cartLines) ? cartLines : [])
        .filter((line) => !isThresholdGiftLine(line))
        .map((line) => line?.merchandise?.id)
        .filter(Boolean),
    ),
  ).sort();
}

function getPresentmentCurrency(api) {
  return getSignalValue(api?.localization?.currency, null)?.isoCode ?? null;
}

/**
 * Marks whether the amounts already are shop-currency amounts. Conversion is
 * skipped only when both currencies are known and equal; an unknown shop
 * currency must never be read as "already in shop currency".
 */
function withPresentmentCurrency(pricing, api) {
  if (!pricing) return null;

  const presentmentCurrency = getPresentmentCurrency(api);
  const provablySameCurrency = Boolean(
    pricing.shopCurrency &&
      presentmentCurrency &&
      pricing.shopCurrency === presentmentCurrency,
  );

  return {
    ...pricing,
    presentmentCurrency,
    needsConversion: !provablySameCurrency,
  };
}

/**
 * Pricing for one evaluation. A buyer paying in the shop currency needs no
 * conversion, so no request is made - this is the common case and it used to
 * cost a backend round trip on every cart change and every Continue click.
 * Only a foreign-currency cart fetches shop prices.
 */
async function resolvePricing(api, cartLines, shopCurrency) {
  const presentmentCurrency = getPresentmentCurrency(api);

  if (shopCurrency && presentmentCurrency && shopCurrency === presentmentCurrency) {
    return withPresentmentCurrency(
      { shopCurrency, prices: {}, complete: true },
      api,
    );
  }

  return fetchShopPricing(api, cartLines);
}

async function fetchShopPricing(api, cartLines) {
  const backendUrl = String(BACKEND_URL_FROM_ENV || "")
    .trim()
    .replace(/\/$/, "");
  const shop = getShopDomain(api);
  const variantIds = getPricedVariantIds(cartLines);
  const failed = { shopCurrency: null, prices: {}, complete: false };

  // Nothing contributes to the threshold, so there is nothing to price.
  if (!variantIds.length) {
    return withPresentmentCurrency(
      { shopCurrency: null, prices: {}, complete: true },
      api,
    );
  }

  if (!backendUrl || !shop) return withPresentmentCurrency(failed, api);

  const requestUrl = `${backendUrl}/api/threshold-shop-prices?shop=${encodeURIComponent(
    shop,
  )}&variantIds=${encodeURIComponent(variantIds.join(","))}`;
  let lastProblem = "Shop price lookup failed.";

  for (let attempt = 0; attempt <= PRICE_RETRY_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) {
      await new Promise((resolve) =>
        setTimeout(resolve, PRICE_RETRY_DELAYS_MS[attempt - 1]),
      );
    }

    try {
      const response = await fetch(requestUrl, { cache: "no-store" });

      if (!response.ok) {
        throw new Error(`Shop price lookup responded with ${response.status}.`);
      }

      const json = await response.json();
      const prices =
        json?.prices && typeof json.prices === "object" ? json.prices : {};
      const complete =
        Boolean(json?.shopCurrency) &&
        variantIds.every((variantId) => Number(prices[variantId]) >= 0);

      if (complete) {
        return withPresentmentCurrency(
          { shopCurrency: json.shopCurrency, prices, complete: true },
          api,
        );
      }

      lastProblem =
        json?.error ||
        "Shop-currency prices were not available for every cart line.";
    } catch (error) {
      lastProblem =
        error instanceof Error ? error.message : "Shop price lookup failed.";
    }
  }

  // Every attempt failed: report once, with the last reason.
  console.error("Threshold shop price lookup failed", lastProblem);
  void logEvent(
    api,
    "warning",
    "threshold_shop_prices_incomplete",
    lastProblem,
    {
      attempts: PRICE_RETRY_DELAYS_MS.length + 1,
      variantIds,
      extensionTarget: EXTENSION_TARGET,
    },
  );

  return withPresentmentCurrency(failed, api);
}

function getCartLineSignature(cartLines) {
  return cartLines
    .map((line) => {
      const attributes = normalizeAttributes(line.attributes)
        .map((attribute) => `${attribute.key}:${attribute.value}`)
        .sort()
        .join("|");

      return `${line.id}:${line.merchandise?.id}:${line.quantity}:${getLineAmount(line)}:${attributes}`;
    })
    .join(",");
}

function toSerializable(value, seen = new WeakSet(), depth = 0) {
  if (
    value == null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value ?? null;
  }

  if (typeof value === "function" || typeof value === "symbol") {
    return undefined;
  }

  if (depth > 6) return "[Max depth reached]";

  if (typeof value === "object") {
    if (seen.has(value)) return "[Circular]";

    seen.add(value);

    if (Array.isArray(value)) {
      return value.map((item) => toSerializable(item, seen, depth + 1));
    }

    return Object.entries(value).reduce((result, [key, entry]) => {
      const serialized = toSerializable(entry, seen, depth + 1);
      if (serialized !== undefined) result[key] = serialized;

      return result;
    }, {});
  }

  return String(value);
}

function serializeCartLine(line) {
  const merchandise = line?.merchandise;

  return {
    id: line?.id ?? null,
    quantity: line?.quantity ?? null,
    isThresholdGift: isThresholdGiftLine(line),
    cost: toSerializable(line?.cost),
    attributes: toSerializable(line?.attributes ?? []),
    discountAllocations: toSerializable(line?.discountAllocations ?? []),
    lineComponents: toSerializable(line?.lineComponents ?? []),
    merchandise: merchandise
      ? {
          id: merchandise.id || null,
          title: merchandise.title || null,
          subtitle: merchandise.subtitle || null,
          sku: merchandise.sku || null,
          product: merchandise.product
            ? {
                id: merchandise.product.id || null,
                vendor: merchandise.product.vendor || null,
                productType: merchandise.product.productType || null,
              }
            : null,
        }
      : null,
    raw: toSerializable(line),
  };
}

/**
 * Full snapshot of the checkout, attached to every activity log entry so an
 * order that shipped without its free gift can be reconstructed afterwards.
 */
function getCheckoutData(api) {
  const cartLines = getSignalValue(api?.lines, []) ?? [];
  const shippingAddress = getSignalValue(api?.shippingAddress, null);
  const attributes = getSignalValue(api?.attributes, []) ?? [];
  const discountCodes = getSignalValue(api?.discountCodes, []) ?? [];
  const giftCards = getSignalValue(api?.appliedGiftCards, []) ?? [];
  const customer = getSignalValue(api?.buyerIdentity?.customer, null);

  return {
    checkoutToken: getCheckoutToken(api),
    activeStep:
      getSignalValue(api?.buyerJourney?.activeStep, null)?.handle ?? null,
    cost: toSerializable(getSignalValue(api?.cost?.totalAmount, null)),
    subtotal: toSerializable(getSignalValue(api?.cost?.subtotalAmount, null)),
    currency: getSignalValue(api?.localization?.currency, null)?.isoCode ?? null,
    presentmentCurrency: getPresentmentCurrency(api),
    // Shop-currency figures are recorded on the threshold_gift_evaluated
    // event, which carries the pricing used for that decision.
    country: getSignalValue(api?.localization?.country, null)?.isoCode ?? null,
    language: getSignalValue(api?.localization?.language, null)?.isoCode ?? null,
    note: getSignalValue(api?.note, null) ?? null,
    attributes: toSerializable(attributes),
    discountCodes: toSerializable(discountCodes),
    giftCards: toSerializable(giftCards),
    customerEmail: getSignalValue(api?.buyerIdentity?.email, null) ?? null,
    customerId: customer?.id ?? null,
    shippingAddress: toSerializable(shippingAddress),
    lineCount: cartLines.length,
    thresholdGiftLineCount: cartLines.filter(isThresholdGiftLine).length,
    lines: cartLines.map(serializeCartLine),
  };
}

// `keepalive` lets the request finish after checkout navigates or the order
// completes; a plain fetch is cancelled on unload and the entry is lost.
// Keepalive bodies are capped at 64KB by the browser, so oversized payloads
// are trimmed rather than dropped.
const MAX_LOG_BODY_BYTES = 60000;

function serializeLogBody(body) {
  let text = JSON.stringify(body);

  if (text.length <= MAX_LOG_BODY_BYTES) return text;

  const checkoutData = body?.payload?.checkoutData;

  if (checkoutData?.lines) {
    text = JSON.stringify({
      ...body,
      payload: {
        ...body.payload,
        checkoutDataTruncated: true,
        checkoutData: { ...checkoutData, lines: undefined },
      },
    });

    if (text.length <= MAX_LOG_BODY_BYTES) return text;
  }

  return JSON.stringify({
    shop: body.shop,
    source: body.source,
    extensionTarget: body.extensionTarget,
    level: body.level,
    eventType: body.eventType,
    message: body.message,
    checkoutToken: body.checkoutToken,
    payload: { payloadTruncated: true },
  });
}

async function sendEventLog(backendUrl, body) {
  const response = await fetch(`${backendUrl}/api/app-event-log`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: serializeLogBody(body),
    keepalive: true,
  });

  if (!response.ok) {
    console.error(
      "Event log rejected",
      response.status,
      await response.text().catch(() => ""),
    );
  }
}

async function logEvent(api, level, eventType, message, payload = {}) {
  const backendUrl = String(BACKEND_URL_FROM_ENV || "")
    .trim()
    .replace(/\/$/, "");
  const shop = getShopDomain(api);

  if (!backendUrl || !shop) return;

  try {
    await sendEventLog(backendUrl, {
        shop,
        source: EXTENSION_SOURCE,
        extensionTarget: EXTENSION_TARGET,
        level,
        eventType,
        message,
        checkoutToken: getCheckoutToken(api),
        customerEmail:
          getSignalValue(api?.buyerIdentity?.email, null) ?? null,
        payload: {
          ...payload,
          checkoutData: payload.checkoutData ?? getCheckoutData(api),
        },
      });
  } catch (error) {
    console.error("Threshold gift log failed", error);
  }
}

async function fetchThresholdGiftConfig(api) {
  const backendUrl = String(BACKEND_URL_FROM_ENV || "")
    .trim()
    .replace(/\/$/, "");
  const shop = getShopDomain(api);

  if (!backendUrl || !shop) {
    return {
      rules: [],
      disallowGiftCardProducts: false,
      giftPicker: DEFAULT_GIFT_PICKER,
    };
  }

  const response = await fetch(
    `${backendUrl}/api/threshold-gift-config?shop=${encodeURIComponent(shop)}`,
    // Belt and braces with the route's `Cache-Control: no-store`: a stale
    // rule set must never be replayed from the HTTP cache.
    { cache: "no-store" },
  );

  if (!response.ok) {
    // Throwing lets the caller retry. Returning an empty config here used to
    // permanently disable gifts for the session after a single backend blip.
    throw new Error(
      `Threshold gift config responded with ${response.status}.`,
    );
  }

  const json = await response.json();

  const rules = Array.isArray(json?.rules)
    ? json.rules.flatMap((rule) => {
        const thresholdAmount = Number(rule?.thresholdAmount);
        const giftVariantId =
          typeof rule?.giftVariantId === "string" ? rule.giftVariantId : "";
        const giftQuantity = Number(rule?.giftQuantity);

        return Number.isFinite(thresholdAmount) &&
          thresholdAmount > 0 &&
          giftVariantId
          ? [
              {
                thresholdAmount,
                giftVariantId,
                giftQuantity:
                  Number.isFinite(giftQuantity) && giftQuantity > 0
                    ? Math.trunc(giftQuantity)
                    : 1,
              },
            ]
          : [];
      })
    : [];

  return {
    rules,
    shopCurrency:
      typeof json?.shopCurrency === "string" && json.shopCurrency
        ? json.shopCurrency
        : null,
    disallowGiftCardProducts: Boolean(json?.disallowGiftCardProducts),
    giftPicker: {
      enabled: Boolean(json?.giftPicker?.enabled),
      heading: json?.giftPicker?.heading || DEFAULT_GIFT_PICKER.heading,
      description:
        json?.giftPicker?.description || DEFAULT_GIFT_PICKER.description,
      buttonText:
        json?.giftPicker?.buttonText || DEFAULT_GIFT_PICKER.buttonText,
    },
  };
}

/**
 * When the buyer pays in a different currency and the conversion could not be
 * resolved, the cart's shop-currency value is unknown. Comparing the raw
 * presentment number with the threshold would be wrong in either direction,
 * so no rule is treated as met until the conversion resolves. A `null`
 * conversion means none was needed (same-currency fast path).
 */
function isThresholdEvaluable(pricing) {
  // Prices are only needed when the buyer pays in a different currency than
  // the shop. Until they are known, no threshold can be judged met.
  if (!pricing?.needsConversion) return true;

  return Boolean(pricing.complete);
}

/**
 * Rules the cart currently qualifies for, using the same comparison as the
 * auto-add path so the picker never offers an ineligible gift.
 */
function getEligibleRules(
  cartLines,
  rules,
  disallowGiftCardProducts,
  pricing = null,
) {
  if (!isThresholdEvaluable(pricing)) return [];

  const baseAmount = getThresholdBaseAmount(
    cartLines,
    disallowGiftCardProducts,
    pricing,
  );

  return rules.filter((rule) => baseAmount >= rule.thresholdAmount);
}

function buildSyncChanges(
  cartLines,
  rules,
  disallowGiftCardProducts = false,
  giftPickerEnabled = false,
  pricing = null,
) {
  // With the picker on, the customer chooses the gift, so nothing is
  // auto-added. Picks that are no longer eligible are removed, and a pick
  // whose quantity exceeds the configured one is trimmed back down.
  if (giftPickerEnabled) {
    const allowedQuantityByVariant = getEligibleRules(
      cartLines,
      rules,
      disallowGiftCardProducts,
      pricing,
    ).reduce((result, rule) => {
      const giftQuantity =
        Number.isFinite(rule.giftQuantity) && rule.giftQuantity > 0
          ? Math.trunc(rule.giftQuantity)
          : 1;

      // Matches the auto-add path: several rules granting the same variant
      // add up to the total allowance.
      result[rule.giftVariantId] =
        (result[rule.giftVariantId] ?? 0) + giftQuantity;

      return result;
    }, {});

    const pickerChanges = [];
    const seenGiftVariantIds = new Set();

    for (const line of cartLines) {
      if (!isThresholdGiftLine(line)) continue;

      const variantId = line.merchandise?.id;
      const allowedQuantity = variantId
        ? (allowedQuantityByVariant[variantId] ?? 0)
        : 0;

      // Not eligible any more, or a second line for a gift already kept.
      if (allowedQuantity <= 0 || seenGiftVariantIds.has(variantId)) {
        pickerChanges.push({
          type: "removeCartLine",
          id: line.id,
          quantity: line.quantity,
        });
        continue;
      }

      seenGiftVariantIds.add(variantId);

      // Drop anything above the quantity configured on the rule.
      if (line.quantity > allowedQuantity) {
        pickerChanges.push({
          type: "updateCartLine",
          id: line.id,
          quantity: allowedQuantity,
        });
      }
    }

    return {
      baseAmount: getThresholdBaseAmount(cartLines, disallowGiftCardProducts, pricing),
      changes: pickerChanges,
    };
  }

  const managedGiftVariantIds = new Set(
    rules.map((rule) => rule.giftVariantId).filter(Boolean),
  );

  if (!rules.length) {
    return {
      baseAmount: getThresholdBaseAmount(cartLines, disallowGiftCardProducts, pricing),
      changes: cartLines.filter(isThresholdGiftLine).map((line) => ({
        type: "removeCartLine",
        id: line.id,
        quantity: line.quantity,
      })),
    };
  }

  const baseAmount = getThresholdBaseAmount(
    cartLines,
    disallowGiftCardProducts,
    pricing,
  );
  // Highest qualifying threshold per gift, used for the attribute value when
  // more than one rule grants the same variant.
  const thresholdAmountByGift = {};
  const thresholdEvaluable = isThresholdEvaluable(pricing);
  const desiredQuantitiesByGift = rules.reduce((result, rule) => {
    if (thresholdEvaluable && baseAmount >= rule.thresholdAmount) {
      const giftQuantity =
        Number.isFinite(rule.giftQuantity) && rule.giftQuantity > 0
          ? Math.trunc(rule.giftQuantity)
          : 1;

      result[rule.giftVariantId] =
        (result[rule.giftVariantId] ?? 0) + giftQuantity;
      thresholdAmountByGift[rule.giftVariantId] = Math.max(
        thresholdAmountByGift[rule.giftVariantId] ?? 0,
        rule.thresholdAmount,
      );
    }

    return result;
  }, {});
  const managedGiftLinesByVariant = {};
  const unmanagedGiftLinesByVariant = {};

  for (const line of cartLines) {
    const variantId = line.merchandise?.id;
    if (!variantId || !managedGiftVariantIds.has(variantId)) continue;

    const collection = isThresholdGiftLine(line)
      ? managedGiftLinesByVariant
      : unmanagedGiftLinesByVariant;

    collection[variantId] ??= [];
    collection[variantId].push({
      id: line.id,
      quantity: line.quantity,
      attributes: line.attributes ?? [],
    });
  }

  const changes = [];

  for (const giftVariantId of managedGiftVariantIds) {
    const desiredQuantity = desiredQuantitiesByGift[giftVariantId] ?? 0;
    const managedGiftLines = managedGiftLinesByVariant[giftVariantId] ?? [];
    const unmanagedGiftLines = unmanagedGiftLinesByVariant[giftVariantId] ?? [];

    if (desiredQuantity <= 0) {
      for (const giftLine of managedGiftLines) {
        changes.push({
          type: "removeCartLine",
          id: giftLine.id,
          quantity: giftLine.quantity,
        });
      }
      continue;
    }

    if (managedGiftLines.length === 0) {
      const [primaryUnmanagedGiftLine, ...extraUnmanagedGiftLines] =
        unmanagedGiftLines;

      if (primaryUnmanagedGiftLine) {
        changes.push({
          type: "updateCartLine",
          id: primaryUnmanagedGiftLine.id,
          quantity: desiredQuantity,
          attributes: withThresholdAttribute(
            primaryUnmanagedGiftLine.attributes,
            thresholdAmountByGift[giftVariantId],
          ),
        });

        for (const extraGiftLine of extraUnmanagedGiftLines) {
          changes.push({
            type: "removeCartLine",
            id: extraGiftLine.id,
            quantity: extraGiftLine.quantity,
          });
        }

        continue;
      }

      changes.push({
        type: "addCartLine",
        merchandiseId: giftVariantId,
        quantity: desiredQuantity,
        attributes: [
          createThresholdAttribute(thresholdAmountByGift[giftVariantId]),
        ],
      });
      continue;
    }

    // The gift line already exists, so any separately added line of the same
    // variant is a duplicate of the gift and is removed: the buyer keeps one
    // line, at the quantity the rule grants.
    for (const giftLine of unmanagedGiftLines) {
      changes.push({
        type: "removeCartLine",
        id: giftLine.id,
        quantity: giftLine.quantity,
      });
    }

    const [primaryGiftLine, ...extraGiftLines] = managedGiftLines;

    // Compare the line that is kept, not the sum: with a duplicate line the
    // sum differs while the primary is already correct, which fired a
    // pointless cart mutation on every sync.
    if (primaryGiftLine.quantity !== desiredQuantity) {
      changes.push({
        type: "updateCartLine",
        id: primaryGiftLine.id,
        quantity: desiredQuantity,
        attributes: withThresholdAttribute(
          primaryGiftLine.attributes,
          thresholdAmountByGift[giftVariantId],
        ),
      });
    }

    for (const extraGiftLine of extraGiftLines) {
      changes.push({
        type: "removeCartLine",
        id: extraGiftLine.id,
        quantity: extraGiftLine.quantity,
      });
    }
  }

  return { baseAmount, changes };
}

// Config is fetched once by the sync loop and shared with the rendered picker.
let giftPickerState = {
  rules: [],
  disallowGiftCardProducts: false,
  giftPicker: DEFAULT_GIFT_PICKER,
  pricing: null,
  blockMessage: "",
};
const giftPickerListeners = new Set();

/**
 * The pending-gift notice is rendered by this extension rather than handed to
 * checkout through the interceptor's `errors`. Checkout owns those and only
 * re-evaluates them when the buyer next tries to progress, so the notice used
 * to stay on screen after the gift had already been added.
 */
function setBlockMessage(message) {
  if (giftPickerState.blockMessage === message) return;

  publishGiftPickerState({ ...giftPickerState, blockMessage: message });
}

function publishGiftPickerState(nextState) {
  giftPickerState = nextState;
  giftPickerListeners.forEach((listener) => listener(nextState));
}

async function fetchGiftProducts(api, variantIds) {
  if (typeof api?.query !== "function" || !variantIds.length) return {};

  const { data } = await api.query(
    `query getThresholdGiftProducts($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          title
          image {
            url
          }
          product {
            title
            featuredImage {
              url
            }
          }
        }
      }
    }`,
    { variables: { ids: variantIds } },
  );

  const nodes = Array.isArray(data?.nodes) ? data.nodes : [];
  const missing = variantIds.filter(
    (id) => !nodes.some((node) => node?.id === id),
  );

  if (missing.length) {
    // Usually means the gift variant is not published to the sales channel
    // this checkout uses, so the Storefront API returns no data for it.
    console.warn(
      "[threshold-gift-picker] no product data for gift variants",
      missing,
    );
  }

  return nodes.reduce((result, node) => {
    if (!node?.id) return result;

    result[node.id] = {
      title: node.product?.title || node.title || "Free gift",
      variantTitle:
        node.title && node.title !== "Default Title" ? node.title : "",
      imageUrl: node.image?.url || node.product?.featuredImage?.url || "",
    };

    return result;
  }, {});
}

function GiftPicker({ api }) {
  const [state, setState] = useState(giftPickerState);
  const [, forceRender] = useState(0);
  const [products, setProducts] = useState({});
  const [selectingVariantId, setSelectingVariantId] = useState("");

  useEffect(() => {
    // The config may already have arrived before this effect ran, so take the
    // current value as well as subscribing to later ones.
    setState(giftPickerState);
    giftPickerListeners.add(setState);

    return () => {
      giftPickerListeners.delete(setState);
    };
  }, []);

  useEffect(() => {
    // `subscribe` is only used as a re-render trigger: the lines are always
    // read fresh from the signal, exactly like the sync loop does.
    const rerender = () => forceRender((value) => value + 1);
    const unsubscribers = [
      api.lines?.subscribe?.(rerender),
      api.localization?.currency?.subscribe?.(rerender),
      api.localization?.country?.subscribe?.(rerender),
    ].filter((fn) => typeof fn === "function");

    return () => unsubscribers.forEach((fn) => fn());
  }, [api]);

  const cartLines = getSignalValue(api.lines, []) ?? [];
  const { rules, disallowGiftCardProducts, giftPicker, pricing, blockMessage } =
    state;
  // Wait for the conversion so a foreign-currency buyer never sees the
  // picker flash on a raw presentment comparison.
  const eligibleRules =
    giftPicker.enabled && pricing
      ? getEligibleRules(cartLines, rules, disallowGiftCardProducts, pricing)
      : [];
  const eligibleVariantIds = Array.from(
    new Set(eligibleRules.map((rule) => rule.giftVariantId)),
  );
  const eligibleKey = eligibleVariantIds.join(",");
  const addedVariantIds = new Set(
    cartLines
      .filter(isThresholdGiftLine)
      .map((line) => line.merchandise?.id)
      .filter(Boolean),
  );
  // Every variant in the cart, gift line or normal purchase, so a gift is
  // never added a second time alongside one the customer already has.
  const cartVariantIds = new Set(
    cartLines.map((line) => line.merchandise?.id).filter(Boolean),
  );
  // True as soon as any eligible gift variant is in the cart, whether the
  // picker added it or the customer already had it.
  const hasGiftVariantInCart = eligibleRules.some((rule) =>
    cartVariantIds.has(rule.giftVariantId),
  );

  useEffect(() => {
    if (!eligibleVariantIds.length) {
      setProducts({});
      return;
    }

    let cancelled = false;

    void fetchGiftProducts(api, eligibleVariantIds)
      .then((nextProducts) => {
        if (!cancelled) setProducts(nextProducts);
      })
      .catch((error) => {
        console.error("Failed to load threshold gift products", error);
      });

    return () => {
      cancelled = true;
    };
  }, [api, eligibleKey]);

  async function toggleGift(rule, isAdded) {
    if (selectingVariantId) return;
    if (!canApplyLineChange(api, isAdded ? "removeCartLine" : "addCartLine")) {
      return;
    }

    // Adding is a no-op when the variant is already in the cart; only the
    // gift line the picker created can be removed again.
    if (
      !isAdded &&
      cartLines.some((line) => line.merchandise?.id === rule.giftVariantId)
    ) {
      return;
    }

    setSelectingVariantId(rule.giftVariantId);

    try {
      const existingLine = cartLines.find(
        (line) =>
          isThresholdGiftLine(line) &&
          line.merchandise?.id === rule.giftVariantId,
      );

      // Multiple gifts can be picked, so adding one never removes another.
      const result = isAdded
        ? existingLine
          ? await api.applyCartLinesChange({
              type: "removeCartLine",
              id: existingLine.id,
              quantity: existingLine.quantity,
            })
          : null
        : await api.applyCartLinesChange({
            type: "addCartLine",
            merchandiseId: rule.giftVariantId,
            quantity:
              Number.isFinite(rule.giftQuantity) && rule.giftQuantity > 0
                ? Math.trunc(rule.giftQuantity)
                : 1,
            attributes: [createThresholdAttribute(rule.thresholdAmount)],
          });

      if (result?.type === "error") {
        console.error("Threshold gift picker failed", result.message);
        void logEvent(
          api,
          "error",
          "threshold_gift_picker_error",
          result.message,
          {
            giftVariantId: rule.giftVariantId,
            extensionTarget: EXTENSION_TARGET,
          },
        );
        return;
      }

      void logEvent(
        api,
        "info",
        isAdded ? "threshold_gift_unpicked" : "threshold_gift_picked",
        isAdded
          ? "Customer removed a threshold gift from the picker."
          : "Customer added a threshold gift from the picker.",
        {
          giftVariantId: rule.giftVariantId,
          extensionTarget: EXTENSION_TARGET,
        },
      );
    } finally {
      setSelectingVariantId("");
      // The lines signal can lag the cart mutation, so re-read it shortly
      // after; otherwise the trigger button keeps rendering against a cart
      // that does not show the new gift line yet.
      setTimeout(
        () => forceRender((value) => value + 1),
        SYNC_RETRY_DELAY_MS,
      );
    }
  }

  if (
    !giftPicker.enabled ||
    !eligibleRules.length ||
    !canApplyLineChange(api, "addCartLine")
  ) {
    console.log("[threshold-gift-picker] not rendering", {
      giftPickerEnabled: giftPicker.enabled,
      ruleCount: rules.length,
      eligibleCount: eligibleRules.length,
      cartLineCount: cartLines.length,
      baseAmount: getThresholdBaseAmount(cartLines, disallowGiftCardProducts, pricing),
      thresholds: rules.map((rule) => rule.thresholdAmount),
    });

    return blockMessage ? (
      <s-banner tone="critical">{blockMessage}</s-banner>
    ) : null;
  }

  console.log("[threshold-gift-picker] render", {
    triggerVisible: !hasGiftVariantInCart,
    addedVariantIds: [...addedVariantIds],
    cartVariantIds: [...cartVariantIds],
    giftLineCount: cartLines.filter(isThresholdGiftLine).length,
    cartLineCount: cartLines.length,
  });

  return (
    <s-stack direction="block" gap="base">
      {blockMessage ? (
        <s-banner tone="critical">{blockMessage}</s-banner>
      ) : null}
      {/* The trigger is hidden as soon as a gift variant is in the cart, by
          the picker or already purchased. The modal stays mounted so an open
          picker is not torn away mid-selection. */}
      {!hasGiftVariantInCart ? (
        <s-button
          variant="primary"
          commandFor={GIFT_PICKER_MODAL_ID}
          command="--show"
        >
          {giftPicker.buttonText}
        </s-button>
      ) : null}

      <s-modal id={GIFT_PICKER_MODAL_ID} heading={giftPicker.heading}>
        <s-stack direction="block" gap="base">
          {/* The modal chrome renders the heading; the description sits
              directly below it. */}
          <s-text>{giftPicker.description}</s-text>

          {eligibleRules.map((rule, index) => {
            const product = products[rule.giftVariantId];
            const isAdded = addedVariantIds.has(rule.giftVariantId);
            const alreadyInCart =
              !isAdded && cartVariantIds.has(rule.giftVariantId);
            const title = product?.title || "Free gift";

            return (
              <s-stack key={rule.giftVariantId} direction="block" gap="base">
                {index > 0 ? <s-divider /> : null}
                <s-stack
                  direction="inline"
                  gap="base"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <s-stack direction="inline" gap="base" alignItems="center">
                    <s-product-thumbnail
                      src={product?.imageUrl || undefined}
                      alt={title}
                      size="base"
                    />
                    <s-stack direction="block" gap="small-500">
                      <s-text>{title}</s-text>
                      {product?.variantTitle ? (
                        <s-text color="subdued">{product.variantTitle}</s-text>
                      ) : null}
                    </s-stack>
                  </s-stack>
                  <s-button
                    variant={isAdded ? "secondary" : "primary"}
                    disabled={Boolean(selectingVariantId) || alreadyInCart}
                    loading={selectingVariantId === rule.giftVariantId}
                    onClick={() => toggleGift(rule, isAdded)}
                  >
                    {isAdded ? "Remove" : alreadyInCart ? "In cart" : "Confirm"}
                  </s-button>
                </s-stack>
              </s-stack>
            );
          })}
        </s-stack>
      </s-modal>
    </s-stack>
  );
}

export default function (...args) {
  const api = getCheckoutApi(args);

  if (!api) {
    console.error("Threshold gift extension could not access Checkout API.");
    return;
  }

  let rules = [];
  let shopCurrency = null;
  let disallowGiftCardProducts = false;
  let giftPicker = DEFAULT_GIFT_PICKER;
  let configLoaded = false;
  let isSyncing = false;
  let cartNeedsSync = false;
  // Guards the whole async body. `isSyncing` only covers the cart-mutation
  // phase, so without this a second run could enter while the first was
  // awaiting prices and both would add the same gift.
  let syncInFlight = false;
  let resyncQueued = false;
  let lineChangesUnsupportedLogged = false;
  let firstBlockedAt = null;
  let lastSignature = "";
  let retryTimer;
  let syncTimer;

  const scheduleSync = (delay = 0) => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      void syncThresholdGiftLines();
    }, delay);
  };

  const setSyncState = ({ syncing, needsSync }) => {
    isSyncing = syncing;
    cartNeedsSync = needsSync;
  };

  async function syncThresholdGiftLines() {
    if (!configLoaded) return;

    // A trigger that arrives mid-sync is queued, never discarded.
    if (syncInFlight) {
      resyncQueued = true;
      return;
    }

    syncInFlight = true;

    try {
      await runThresholdGiftSync();
    } finally {
      syncInFlight = false;

      if (resyncQueued) {
        resyncQueued = false;
        scheduleSync();
      }
    }
  }

  async function runThresholdGiftSync() {
    const cartLines = getSignalValue(api.lines, []);
    // Change detection first, so an unchanged cart costs no network request
    // even though prices are never cached.
    const signature = `${JSON.stringify(rules)}::${disallowGiftCardProducts}::${giftPicker.enabled}::${getPresentmentCurrency(api) ?? ""}::${getCartLineSignature(cartLines)}`;

    if (signature === lastSignature && !cartNeedsSync) return;
    lastSignature = signature;

    // Threshold amounts are configured in the shop currency, so the cart is
    // normalised before any rule is evaluated.
    const pricing = await resolvePricing(api, cartLines, shopCurrency);

    const { baseAmount, changes: plannedChanges } = buildSyncChanges(
      cartLines,
      rules,
      disallowGiftCardProducts,
      giftPicker.enabled,
      pricing,
    );
    const changes = filterSupportedLineChanges(api, plannedChanges);

    if (changes.length < plannedChanges.length && !lineChangesUnsupportedLogged) {
      lineChangesUnsupportedLogged = true;
      void logEvent(
        api,
        "warning",
        "threshold_gift_line_changes_unsupported",
        "This checkout does not allow line item changes (e.g. a draft order checkout); threshold gifts are skipped.",
        {
          skippedChanges: plannedChanges
            .filter((change) => !canApplyLineChange(api, change.type))
            .map((change) => change.type),
          instructions: getSignalValue(api?.instructions, null)?.lines ?? null,
          extensionTarget: EXTENSION_TARGET,
        },
      );
    }

    // Logged only when the cart has to change: an unchanged evaluation is the
    // steady state and used to cost a network request each time.
    if (changes.length) void logEvent(
      api,
      "info",
      "threshold_gift_evaluated",
      "Threshold rules were evaluated in the shop currency.",
      {
        baseAmountShopCurrency: baseAmount,
        shopCurrency: pricing?.shopCurrency ?? null,
        presentmentCurrency: pricing?.presentmentCurrency ?? null,
        needsConversion: pricing?.needsConversion ?? null,
        shopPricesComplete: pricing?.complete ?? null,
        thresholdEvaluable: isThresholdEvaluable(pricing),
        thresholds: rules.map((rule) => rule.thresholdAmount),
        eligibleThresholds: getEligibleRules(
          cartLines,
          rules,
          disallowGiftCardProducts,
          pricing,
        ).map((rule) => rule.thresholdAmount),
        plannedChanges: changes.map((change) => change.type),
        extensionTarget: EXTENSION_TARGET,
      },
    );

    if (!changes.length) {
      // Cart already matches the rules: nothing pending, so drop the notice.
      setBlockMessage("");
      setSyncState({ syncing: false, needsSync: false });
      return;
    }

    setSyncState({ syncing: true, needsSync: true });

    let failed = false;

    try {
      for (const change of changes) {
        const result = await api.applyCartLinesChange(change);

        if (result.type === "error") {
          if (isUnsupportedLineChangeError(result.message)) {
            // This checkout refuses line changes: stop, and never hold checkout.
            lineChangesLocked = true;
            setBlockMessage("");
            void logEvent(
              api,
              "warning",
              "threshold_gift_line_changes_unsupported",
              result.message,
              { change, extensionTarget: EXTENSION_TARGET },
            );
            break;
          }

          failed = true;
          console.error("Threshold gift sync failed", result.message);
          void logEvent(
            api,
            "error",
            "threshold_gift_sync_error",
            result.message,
            {
              change,
              baseAmount,
              extensionTarget: EXTENSION_TARGET,
            },
          );
          break;
        }

        const eventDetails =
          change.type === "addCartLine"
            ? {
                eventType: "threshold_gift_auto_added",
                message: "Threshold gift was auto-added to checkout.",
                variantId: change.merchandiseId,
                quantity: change.quantity,
              }
            : change.type === "removeCartLine"
              ? {
                  eventType: "threshold_gift_auto_removed",
                  message: "Threshold gift was auto-removed from checkout.",
                  lineId: change.id,
                  quantity: change.quantity,
                }
              : change.type === "updateCartLine"
                ? {
                    eventType: "threshold_gift_qty_updated",
                    message:
                      "Threshold gift quantity was updated automatically.",
                    lineId: change.id,
                    quantity: change.quantity,
                  }
                : null;

        if (eventDetails) {
          void logEvent(
            api,
            "info",
            eventDetails.eventType,
            eventDetails.message,
            {
              ...eventDetails,
              baseAmount,
              extensionTarget: EXTENSION_TARGET,
            },
          );
        }
      }
    } finally {
      setSyncState({ syncing: false, needsSync: failed });
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        if (!failed) {
          setSyncState({ syncing: false, needsSync: false });
        }
        scheduleSync();
      }, SYNC_RETRY_DELAY_MS);
    }
  }

  api.lines?.subscribe?.(() => scheduleSync());
  // A currency or market switch changes the presentment -> shop-currency
  // rate, so the threshold is re-evaluated even if the lines signal itself
  // does not re-emit.
  api.localization?.currency?.subscribe?.(() => scheduleSync());
  api.localization?.country?.subscribe?.(() => scheduleSync());

  // Without this the buyer can reach "Pay now" before the gift line has been
  // applied, and the order completes without the free product. Blocking is
  // driven by the desired-state diff, so it clears itself as soon as the cart
  // matches the rules.
  void api.buyerJourney?.intercept?.(async ({ canBlockProgress }) => {
    if (!canBlockProgress) return { behavior: "allow" };

    const cartLines = getSignalValue(api.lines, []) ?? [];
    // Interceptors may be async, so the decision is made against freshly
    // fetched prices rather than a cached snapshot.
    const pricing = configLoaded
      ? await resolvePricing(api, cartLines, shopCurrency)
      : null;
    const pricingReady = Boolean(pricing) && isThresholdEvaluable(pricing);
    const pendingChanges =
      configLoaded && pricingReady
        ? filterSupportedLineChanges(
            api,
            buildSyncChanges(
              cartLines,
              rules,
              disallowGiftCardProducts,
              giftPicker.enabled,
              pricing,
            ).changes,
          )
        : [];

    const notReady =
      !configLoaded ||
      !pricingReady ||
      isSyncing ||
      cartNeedsSync ||
      pendingChanges.length > 0;

    if (!notReady) {
      firstBlockedAt = null;

      return {
        behavior: "allow",
        perform: () => setBlockMessage(""),
      };
    }

    // Make sure something is actually working on it.
    scheduleSync();

    firstBlockedAt ??= Date.now();

    // Never hold checkout forever: if gifts cannot be resolved, let the buyer
    // pay rather than stranding them. The gift is simply not granted.
    if (Date.now() - firstBlockedAt > MAX_BLOCK_MS) {
      void logEvent(
        api,
        "error",
        "threshold_gift_block_timeout",
        "Allowing checkout without resolving threshold gifts after timeout.",
        {
          configLoaded,
          pricingReady,
          isSyncing,
          cartNeedsSync,
          pendingChanges,
          extensionTarget: EXTENSION_TARGET,
        },
      );

      return {
        behavior: "allow",
        perform: () => setBlockMessage(""),
      };
    }

    {
      void logEvent(
        api,
        "warning",
        "threshold_gift_checkout_blocked",
        "Checkout was blocked because threshold gifts are not applied yet.",
        {
          configLoaded,
          pricingReady,
          isSyncing,
          cartNeedsSync,
          pendingChanges,
          extensionTarget: EXTENSION_TARGET,
        },
      );

      return {
        behavior: "block",
        reason: "threshold_gift_sync_in_progress",
        // Rendered by this extension so it can be cleared the moment the
        // gift lands, instead of lingering in checkout's own error area.
        perform: (result) => {
          setBlockMessage(
            result.behavior === "block" ? SYNC_PENDING_MESSAGE : "",
          );
        },
      };
    }
  });

  let configAttempt = 0;

  function loadThresholdGiftConfig() {
    void fetchThresholdGiftConfig(api)
      .then((config) => {
        configAttempt = 0;
        rules = config.rules;
        shopCurrency = config.shopCurrency;
        disallowGiftCardProducts = config.disallowGiftCardProducts;
        giftPicker = config.giftPicker;
        configLoaded = true;
        publishGiftPickerState({
          ...giftPickerState,
          rules,
          disallowGiftCardProducts,
          giftPicker,
        });
        scheduleSync();
      })
      .catch((error) => {
        console.error("Failed to fetch threshold gift config", error);
        void logEvent(
          api,
          "error",
          "threshold_gift_config_failed",
          error instanceof Error ? error.message : "Config load failed.",
          { attempt: configAttempt + 1, extensionTarget: EXTENSION_TARGET },
        );

        // Keep retrying with backoff. `configLoaded` stays false, so the
        // buyer-journey interceptor holds checkout rather than letting an
        // order through without the gift it qualifies for.
        const delay =
          CONFIG_RETRY_DELAYS_MS[
            Math.min(configAttempt, CONFIG_RETRY_DELAYS_MS.length - 1)
          ];

        configAttempt += 1;
        setTimeout(loadThresholdGiftConfig, delay);
      });
  }

  loadThresholdGiftConfig();

  render(<GiftPicker api={api} />, document.body);
}
